import { CONTRAST_TEXT, CONTRAST_UI, TOKENS, contrastRatio, tokenValue } from '@orgus/tokens';
import type { ThemeName, TokenName } from '@orgus/tokens';
import { Badge, Panel, Tooltip, cn } from '@orgus/ui';
import { usePrefs } from '../../lib/prefs.ts';
import { Spec } from './Spec.tsx';

type Entry = { name: TokenName; css: string; description?: string };

const ELEVATION_ORDER = ['flat', '1', '2', '3'];

function entries(prefix: string, filter: (name: string) => boolean = () => true): Entry[] {
  return (Object.keys(TOKENS) as TokenName[])
    .filter((n) => n.startsWith(prefix) && filter(n))
    .map((name) => {
      const t = TOKENS[name] as { css: string; description?: string };
      return { name, css: t.css, description: t.description };
    });
}

/** Contrast of a text colour on the default surface, graded against WCAG AA. */
function Contrast({ fg, theme, large }: { fg: TokenName; theme: ThemeName; large?: boolean }) {
  const ratio = contrastRatio(tokenValue(fg, theme), tokenValue('color.surface.default', theme));
  const min = large ? CONTRAST_UI : CONTRAST_TEXT;
  const pass = ratio >= min;
  return (
    <Tooltip content={`${ratio.toFixed(2)}:1 on surface. AA needs ${min}:1.`}>
      <span tabIndex={0} className={cn('text-caption num', pass ? 'text-fg-subtle' : 'font-medium text-status-danger')}>
        {ratio.toFixed(1)}:1 {pass ? 'AA' : 'Fails AA'}
      </span>
    </Tooltip>
  );
}

function Swatch({ e, theme, ink }: { e: Entry; theme: ThemeName; ink?: boolean }) {
  const value = tokenValue(e.name, theme);
  return (
    <div className="flex min-w-0 items-center gap-2.5 rounded-card border border-border-subtle bg-bg p-2" title={e.description}>
      <span aria-hidden className="size-8 shrink-0 rounded-control border border-border" style={{ background: `var(${e.css})` }} />
      <span className="flex min-w-0 flex-col">
        <code className="truncate text-caption text-fg">{e.css}</code>
        <span className="flex items-center gap-2">
          <code className="text-caption text-fg-subtle">{value}</code>
          {ink && <Contrast fg={e.name} theme={theme} large={e.name === 'color.status.inactive'} />}
        </span>
      </span>
    </div>
  );
}

function SwatchGrid({ list, theme, ink }: { list: Entry[]; theme: ThemeName; ink?: boolean }) {
  return (
    <div className="grid w-full gap-2 sm:grid-cols-2 xl:grid-cols-3">
      {list.map((e) => (
        <Swatch key={e.name} e={e} theme={theme} ink={ink} />
      ))}
    </div>
  );
}

/** Every token, rendered live from the CSS variables, with the active theme's values and contrast. */
export function TokenCatalogue() {
  const theme = usePrefs((s) => s.theme);
  const density = usePrefs((s) => s.density);
  const isStatus = (n: string) => /^color\.status\.[a-z]+$/.test(n);
  return (
    <div className="grid items-start gap-5 xl:grid-cols-2" data-catalogue="tokens">
      <Panel title="Colour" sub={`${theme} theme`} className="xl:col-span-2">
        <Spec label="Surfaces">
          <SwatchGrid list={[...entries('color.bg.'), ...entries('color.surface.'), ...entries('color.border.')]} theme={theme} />
        </Spec>
        <Spec label="Text">
          <SwatchGrid list={entries('color.fg.', (n) => n !== 'color.fg.faint')} theme={theme} ink />
        </Spec>
        <Spec label="Brand accent">
          <SwatchGrid list={[...entries('color.accent.'), ...entries('color.focus.')]} theme={theme} />
        </Spec>
        <Spec label="Status">
          <SwatchGrid list={entries('color.status.', isStatus)} theme={theme} ink />
        </Spec>
        <Spec label="Data (categorical)">
          <div className="flex flex-wrap gap-1.5">
            {entries('color.data.').map((e) => (
              <Tooltip key={e.name} content={`${e.css} ${tokenValue(e.name, theme)}`}>
                <span tabIndex={0} className="flex h-8 w-14 items-end rounded-control p-1" style={{ background: `var(${e.css})` }}>
                  <span className="rounded-control bg-bg px-1 text-caption text-fg num">{e.css.replace('--data-', '')}</span>
                </span>
              </Tooltip>
            ))}
          </div>
          <p className="w-full text-caption text-fg-subtle">Eight fixed slots for charts and identity, never for status.</p>
        </Spec>
        <Spec label="Live layer">
          <SwatchGrid list={entries('color.live-trace')} theme={theme} />
          <div className="flex flex-wrap gap-2">
            {entries('color.activity.').map((e) => (
              <Tooltip key={e.name} content={`${e.css} ${tokenValue(e.name, theme)}`}>
                <span tabIndex={0} className="flex h-8 w-14 items-end rounded-control border border-border-subtle p-1" style={{ background: `var(${e.css})` }}>
                  <span className="rounded-control bg-bg px-1 text-caption text-fg num">{e.css.replace('--activity-', '')}</span>
                </span>
              </Tooltip>
            ))}
          </div>
          <p className="w-full text-caption text-fg-subtle">The trace marks an edge that carried work in the last minute; the strong trace is a flow dot. The activity ramp shows how busy, from quiet 0 to busiest 4, as lightness only, never a hue.</p>
        </Spec>
      </Panel>

      <Panel title="Type" sub="7 sizes, 2 weights">
        {/* The wall ramp (font-size.wall-*) belongs to the wall surface only, so the console catalogue leaves it out. */}
        {entries('font-size.', (n) => !n.startsWith('font-size.wall-')).map((e) => {
          const key = e.name.split('.')[1] ?? '';
          return (
            <Spec key={e.name} label={<code>{`${key} ${tokenValue(e.name)}`}</code>}>
              <span className="truncate text-fg" style={{ fontSize: `var(${e.css})`, lineHeight: `var(--line-height-${key})` }}>
                4.9K tokens in 3m 12s
              </span>
            </Spec>
          );
        })}
        <Spec label="Mono">
          <code className="text-ui text-fg-muted">9f2c4e1 feat/approvals-inbox C:/repos/qawl</code>
        </Spec>
      </Panel>

      <Panel title="Space, radius, elevation" sub="4 px grid">
        <Spec label="Spacing">
          <div className="flex w-full flex-col gap-1.5">
            {entries('space.', (n) => /^space\.\d+$/.test(n) && n !== 'space.0').map((e) => (
              <div key={e.name} className="flex items-center gap-3">
                <code className="w-20 shrink-0 text-caption text-fg-subtle">{e.css.replace('--', '')}</code>
                <span aria-hidden className="h-2 rounded-pill bg-accent" style={{ width: `var(${e.css})` }} />
                <span className="text-caption text-fg-subtle num">{tokenValue(e.name)}</span>
              </div>
            ))}
          </div>
        </Spec>
        <Spec label="Radius">
          {entries('radius.').map((e) => (
            <span key={e.name} className="flex size-14 items-end border border-border-strong bg-raised p-1 text-caption text-fg-subtle" style={{ borderRadius: `var(${e.css})` }}>
              {e.name.split('.')[1]}
            </span>
          ))}
        </Spec>
        <Spec label="Elevation">
          {ELEVATION_ORDER.flatMap((k) => entries(`elevation.${k}`)).map((e) => (
            <span key={e.name} className="flex h-14 w-20 items-end rounded-card bg-raised p-1.5 text-caption text-fg-subtle" style={{ boxShadow: `var(${e.css})` }}>
              {e.name.split('.')[1]}
            </span>
          ))}
        </Spec>
        <Spec label="Motion">
          {entries('duration.').map((e) => (
            <Badge key={e.name} tone="neutral" variant="outline">
              {e.name.split('.')[1]} {tokenValue(e.name)}
            </Badge>
          ))}
          <span className="w-full text-caption text-fg-subtle">Four curves: standard, emphasised, linear and settle, with every duration 0 ms under reduced motion except fade-reduced.</span>
        </Spec>
        <Spec label="Density">
          <Badge tone="accent">{density}</Badge>
          <span className="text-caption text-fg-subtle num">
            row {tokenValue('row.height', theme, density)}, control {tokenValue('control.height', theme, density)}
          </span>
        </Spec>
      </Panel>
    </div>
  );
}
